import { useCallback } from "react"
import useSWR, { useSWRConfig } from "swr"

import { invalidApiDataError } from "../../../../errors/recognisedErrors"
import { useGetUserData } from "../../../../hooks/useGetUserData"
import { postClientProtected } from "../../clients/clients"
import { installerDraftsMapper } from "../../mappers/installerInterface/installerDraftsMapper"
import { DRAFT_SUMMARY_CONFIG } from "../../swrConfig"
import { IDraftApplicationListResponse } from "../../types/installerInterface/installerApplicationTypes"
import { INSTALLER_LIST_DRAFT_APPLICATIONS } from "../../urls/hybrid"

// A single, non-paginated SWR key so draft create/delete/submit can update the
// cached answer directly (SWR can't reliably target useSWRInfinite keys).
export const HAS_DRAFTS_KEY = "installer-has-drafts"

// Every save sets ttl = lastUpdated + 180 days, so the most recently updated
// draft is always the last to expire. Asking for just that one gives a correct
// answer in a single 1-item request, even though the API filters expired drafts
// after applying the limit.
export const HAS_DRAFTS_SEARCH_PARAMS = "sort_key=lastUpdated&ascending=desc"

/**
 * Returns true if the signed-in installer has at least one live draft.
 * @returns {Promise<boolean>} Whether any drafts exist.
 */
export const fetchHasDrafts = async (): Promise<boolean> => {
    try {
        const response: IDraftApplicationListResponse =
            installerDraftsMapper.mapList(
                await postClientProtected(
                    INSTALLER_LIST_DRAFT_APPLICATIONS(
                        HAS_DRAFTS_SEARCH_PARAMS,
                        1
                    ),
                    null
                )
            )
        return response.data.length > 0
    } catch {
        throw invalidApiDataError(
            "An error occurred while checking for installer drafts."
        )
    }
}

export interface IUseHasDrafts {
    hasDrafts: boolean
    isLoading: boolean
    isError: boolean
}

/**
 * Checks whether the signed-in installer has at least one draft application.
 *
 * Call this from the component that renders the drafts banner rather than from
 * a long-lived provider, so the request is tied to that component's lifetime.
 * It checks each time the banner mounts (not on focus, so it can't pop up while
 * the user is mid-form). Draft delete/submit call `useClearHasDrafts`.
 * @returns {IUseHasDrafts} Whether any drafts exist, plus loading/error state.
 */
export const useHasDrafts = (): IUseHasDrafts => {
    const { isInstaller, isInstallerAdmin } = useGetUserData()

    const { data, error, isLoading } = useSWR(
        isInstaller || isInstallerAdmin ? HAS_DRAFTS_KEY : null,
        fetchHasDrafts,
        DRAFT_SUMMARY_CONFIG
    )

    return {
        hasDrafts: data ?? false,
        isLoading,
        isError: !!error,
    }
}

/**
 * Forgets the cached `useHasDrafts` answer. Call after a draft is deleted or
 * submitted, so the banner can never show a stale "yes"; the next banner mount
 * fetches afresh. Not needed after saving a draft: a stale "no" just means the
 * banner appears once that fetch completes.
 *
 * Uses a 3-argument mutate because a bare `mutate(key)` leaves the cache
 * untouched when no banner is mounted.
 * @returns {() => void} Clears the cached answer.
 */
export const useClearHasDrafts = (): (() => void) => {
    const { mutate } = useSWRConfig()

    return useCallback(() => {
        mutate(HAS_DRAFTS_KEY, undefined, { revalidate: false })
    }, [mutate])
}

export default useHasDrafts
